import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { filesApi } from '@/lib/api';
import { formatFileSize } from '@/constants/utils/files';
import Dropzone from './Dropzone';
import {
  describeFileLimitViolation,
  probeMediaDurationSeconds,
} from '@/constants/utils/file_upload_limits';

function pad2(n) { return String(n).padStart(2, '0'); }
function fmtTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${pad2(s)}`;
}

const HANDLE_WIDTH = 14;

function VideoTrimmer({
  value,
  onChange,
  disabled,
  accept = 'video/*',
  label = 'Choose video',
  limitsHint,
  maxSizeBytes,
  maxDurationSeconds,
}) {
  const { colors } = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const videoRef = useRef(null);
  const trackRef = useRef(null);

  const current = value && typeof value === 'object'
    ? value
    : (value ? { id: value, startTime: 0, endTime: null } : null);

  const startTime = current?.startTime ?? 0;
  const endTime = current?.endTime != null ? current.endTime : duration;

  const fileId = current?.id || current?.videoId || null;
  const videoUrl = current?.cdnPath || current?.url || null;
  // Placeholder presets carry a cdnPath without a real id. Render the player
  // anyway so users can preview the example.
  const isSample = !!(current && !fileId && videoUrl);

  const styles = useMemo(() => StyleSheet.create({
    wrap: { gap: 12 },
    player: {
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: 14,
      backgroundColor: colors.solid_color_tertiary,
      overflow: 'hidden',
      gap: 12,
    },
    videoWrap: {
      width: '100%',
      backgroundColor: '#000',
      aspectRatio: 16 / 9,
      alignItems: 'center',
      justifyContent: 'center',
    },
    videoEl: {
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
    },
    body: { padding: 14, gap: 12 },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    iconBubble: {
      width: 36, height: 36, borderRadius: 999,
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: colors.solid_color_block,
      borderWidth: 1, borderColor: colors.border_color_secondary,
    },
    headerMeta: { flex: 1, minWidth: 0, gap: 2 },
    metaRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    removeBtn: {
      width: 32, height: 32, borderRadius: 999,
      alignItems: 'center', justifyContent: 'center',
      borderWidth: 1, borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },

    controlsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    playButton: {
      width: 36, height: 36, borderRadius: 999,
      alignItems: 'center', justifyContent: 'center',
      // Solid neutral so the button reads against the panel — accent
      // colors blended into the panel bg in some themes.
      backgroundColor: colors.text_color_primary,
    },
    timeText: { fontVariant: ['tabular-nums'] },

    timelineLabel: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    timelineWrap: {
      height: 36,
      borderRadius: 999,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      position: 'relative',
      ...(Platform.OS === 'web' && { userSelect: 'none' }),
    },
    selection: {
      position: 'absolute',
      top: 0, bottom: 0,
      backgroundColor: colors.solid_color_block,
      borderTopWidth: 2,
      borderBottomWidth: 2,
      borderColor: colors.text_color_primary,
    },
    handle: {
      position: 'absolute',
      top: -4, bottom: -4,
      width: HANDLE_WIDTH,
      borderRadius: 6,
      backgroundColor: colors.text_color_primary,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web' && { cursor: 'ew-resize' }),
    },
    handleGrip: {
      width: 2, height: 14, backgroundColor: '#ffffff', borderRadius: 999,
    },
    playhead: {
      position: 'absolute',
      top: -6, bottom: -6,
      width: 2,
      backgroundColor: colors.text_color_primary,
      borderRadius: 1,
    },

    quickRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    quickPill: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      paddingHorizontal: 10, paddingVertical: 6,
      borderRadius: 999,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1, borderColor: colors.border_color_secondary,
    },
  }), [colors]);

  // ---------------------------------------------------------------------------
  // Push trim metadata back up when start/end change. We only patch what
  // actually changed so the rest of the file metadata is preserved.
  // ---------------------------------------------------------------------------
  const commit = useCallback((patch) => {
    if (!current) return;
    onChange?.({ ...current, ...patch });
  }, [current, onChange]);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [videoUrl]);

  // ---------------------------------------------------------------------------
  // Upload + clear
  // ---------------------------------------------------------------------------
  const uploadFile = useCallback(async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    setError(null);
    const probedDuration = await probeMediaDurationSeconds(file);
    const limitError = describeFileLimitViolation(
      { size: file.size, duration: probedDuration },
      { maxSizeBytes, maxDurationSeconds },
    );
    if (limitError) {
      setError(limitError);
      return;
    }
    setIsUploading(true);
    try {
      const { file: saved } = await filesApi.uploadFile({ file });
      if (!saved?.id) throw new Error('Upload failed');
      onChange?.({
        id: saved.id,
        videoId: saved.id,
        name: saved.name || saved.originalName || file.name,
        mimeType: saved.mimeType || file.type,
        sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes ?? file.size,
        cdnPath: saved.cdnPath || null,
        thumbnailPath: saved.thumbnailPath || null,
        fileType: saved.fileType,
        metadata: saved.metadata || null,
        startTime: 0,
        endTime: null,
      });
    } catch (e) {
      setError(e?.message || 'Failed to upload video');
    } finally {
      setIsUploading(false);
    }
  }, [onChange, maxSizeBytes, maxDurationSeconds]);

  const handleLibrary = useCallback((picked) => {
    const saved = picked?.[0];
    if (!saved?.id) return;
    const limitError = describeFileLimitViolation(saved, { maxSizeBytes, maxDurationSeconds });
    if (limitError) {
      setError(limitError);
      return;
    }
    setError(null);
    onChange?.({
      id: saved.id,
      videoId: saved.id,
      name: saved.name || saved.originalName,
      mimeType: saved.mimeType,
      sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes,
      cdnPath: saved.cdnPath || null,
      thumbnailPath: saved.thumbnailPath || null,
      fileType: saved.fileType,
      metadata: saved.metadata || null,
      startTime: 0,
      endTime: null,
    });
  }, [onChange, maxSizeBytes, maxDurationSeconds]);

  const clearFile = useCallback(() => {
    onChange?.(null);
    setIsPlaying(false);
    setCurrentTime(0);
  }, [onChange]);

  // ---------------------------------------------------------------------------
  // Playback controls
  // ---------------------------------------------------------------------------
  const handlePlayPause = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => setError('Could not play video'));
    else v.pause();
  }, []);

  // ---------------------------------------------------------------------------
  // Trim handle drag (web). Native drag could use PanResponder, but the trim
  // metadata still flows through the numeric quick-pills below on native.
  // ---------------------------------------------------------------------------
  const startDrag = useCallback((handle) => (event) => {
    if (Platform.OS !== 'web' || !duration || !trackRef.current) return;
    event.preventDefault();
    const trackRect = trackRef.current.getBoundingClientRect();

    const onMove = (moveEvent) => {
      const clientX = moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX;
      if (clientX == null) return;
      const ratio = Math.max(0, Math.min(1, (clientX - trackRect.left) / trackRect.width));
      const t = ratio * duration;
      if (handle === 'start') {
        commit({ startTime: Math.min(t, (endTime || duration) - 0.1) });
      } else {
        commit({ endTime: Math.max(t, startTime + 0.1) });
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  }, [commit, duration, endTime, startTime]);

  // ---------------------------------------------------------------------------
  // Empty state — dropzone only.
  // ---------------------------------------------------------------------------
  if (!fileId && !isSample) {
    return (
      <View style={styles.wrap}>
        <Dropzone
          accept={accept}
          disabled={disabled}
          isUploading={isUploading}
          icon='film'
          primaryLabel={label || 'Drag and drop a video here'}
          secondaryLabel={limitsHint ? `MP4, MOV, WebM · ${limitsHint}` : 'MP4, MOV, WebM'}
          onFiles={uploadFile}
          onLibrary={handleLibrary}
        />
        {!!error && <Text size='s' color='danger'>{error}</Text>}
      </View>
    );
  }

  const startPct = duration ? (startTime / duration) * 100 : 0;
  const endPct = duration ? ((endTime || duration) / duration) * 100 : 100;
  const playheadPct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <View style={styles.wrap}>
      <View style={styles.player}>
        <View style={styles.videoWrap}>
          {Platform.OS === 'web' && videoUrl ? (
            // eslint-disable-next-line react/forbid-elements
            <video
              ref={videoRef}
              src={videoUrl}
              controls={false}
              preload='metadata'
              onLoadedMetadata={(e) => {
                const d = e.currentTarget.duration || 0;
                setDuration(d);
                if (current && current.endTime == null) commit({ endTime: d });
              }}
              onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime || 0)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              style={styles.videoEl}
            />
          ) : (
            <Icon name='film' size={36} color='#ffffff66' />
          )}
        </View>

        <View style={styles.body}>
          <View style={styles.headerRow}>
            <View style={styles.iconBubble}>
              <Icon name='film' size={14} color={colors.text_color_secondary} />
            </View>
            <View style={styles.headerMeta}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <Text size='s' weight='bold' numberOfLines={1} style={{ flexShrink: 1 }}>
                  {current?.name || (isSample ? 'Sample video' : 'Uploaded video')}
                </Text>
                {isSample && (
                  <View style={{
                    flexDirection: 'row', alignItems: 'center', gap: 3,
                    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 999,
                    backgroundColor: colors.solid_color_block,
                    borderWidth: 1, borderColor: colors.border_color_secondary,
                  }}>
                    <Icon name='sparkles' size={8} color={colors.text_color_secondary} />
                    <Text size='xxs' weight='bold' color='secondary'>SAMPLE</Text>
                  </View>
                )}
              </View>
              <View style={styles.metaRow}>
                <Text size='xs' color='tertiary' numberOfLines={1}>
                  {current?.sizeInBytes ? formatFileSize(current.sizeInBytes) : 'Video file'}
                  {duration > 0 ? `  ·  ${fmtTime(duration)}` : ''}
                </Text>
              </View>
            </View>
            {!disabled && (
              <Pressable onPress={clearFile} style={styles.removeBtn} hitSlop={6}>
                <Icon name='xmark' size={12} color={colors.text_color_secondary} />
              </Pressable>
            )}
          </View>

          <View style={styles.controlsRow}>
            <Pressable onPress={handlePlayPause} style={styles.playButton} disabled={!videoUrl}>
              <Icon name={isPlaying ? 'pause' : 'play'} size={14} color='#ffffff' />
            </Pressable>
            <Text size='s' style={styles.timeText} weight='medium'>
              {fmtTime(currentTime)} <Text color='tertiary'>/ {fmtTime(duration)}</Text>
            </Text>
          </View>

          <View>
            <View style={styles.timelineLabel}>
              <Text size='xs' color='tertiary'>Trim range</Text>
              <Text size='xs' weight='medium' style={styles.timeText}>
                {fmtTime(startTime)} → {fmtTime(endTime || duration)}
              </Text>
            </View>

            {Platform.OS === 'web' ? (
              <div ref={trackRef} style={{ position: 'relative', height: 36, marginTop: 6 }}>
                <View style={styles.timelineWrap}>
                  <View style={[styles.selection, { left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }]} />
                  {duration > 0 && (
                    <View style={[styles.playhead, { left: `${playheadPct}%` }]} />
                  )}
                </View>
                <div
                  onMouseDown={startDrag('start')}
                  onTouchStart={startDrag('start')}
                  style={{
                    position: 'absolute', top: -4, bottom: -4,
                    left: `calc(${startPct}% - ${HANDLE_WIDTH / 2}px)`,
                    width: HANDLE_WIDTH,
                    borderRadius: 6,
                    backgroundColor: colors.text_color_primary,
                    cursor: 'ew-resize',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <View style={styles.handleGrip} />
                </div>
                <div
                  onMouseDown={startDrag('end')}
                  onTouchStart={startDrag('end')}
                  style={{
                    position: 'absolute', top: -4, bottom: -4,
                    left: `calc(${endPct}% - ${HANDLE_WIDTH / 2}px)`,
                    width: HANDLE_WIDTH,
                    borderRadius: 6,
                    backgroundColor: colors.text_color_primary,
                    cursor: 'ew-resize',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <View style={styles.handleGrip} />
                </div>
              </div>
            ) : (
              <View style={[styles.timelineWrap, { marginTop: 6 }]}>
                <View style={[styles.selection, { left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }]} />
                {duration > 0 && (
                  <View style={[styles.playhead, { left: `${playheadPct}%` }]} />
                )}
              </View>
            )}
          </View>

          <View style={styles.quickRow}>
            <Pressable
              onPress={() => commit({ startTime: currentTime })}
              disabled={!duration}
              style={[styles.quickPill, !duration && { opacity: 0.5 }]}
            >
              <Icon name='arrow-down-to-bracket' size={11} color={colors.text_color_secondary} />
              <Text size='xs' weight='medium'>Set start to playhead</Text>
            </Pressable>
            <Pressable
              onPress={() => commit({ endTime: currentTime })}
              disabled={!duration}
              style={[styles.quickPill, !duration && { opacity: 0.5 }]}
            >
              <Icon name='arrow-down-to-bracket' size={11} color={colors.text_color_secondary} />
              <Text size='xs' weight='medium'>Set end to playhead</Text>
            </Pressable>
            <Pressable
              onPress={() => commit({ startTime: 0, endTime: duration || null })}
              disabled={!duration}
              style={[styles.quickPill, !duration && { opacity: 0.5 }]}
            >
              <Icon name='rotate-left' size={11} color={colors.text_color_secondary} />
              <Text size='xs' weight='medium'>Reset trim</Text>
            </Pressable>
          </View>
        </View>
      </View>

      {!!error && <Text size='s' color='danger'>{error}</Text>}
    </View>
  );
}

export default React.memo(VideoTrimmer);
